import { create } from "zustand";
import { getStatus } from "@/features/eservices/api";
import { getFirm, getFirmSettings, listFirmSignatures } from "@/features/settings/firm-api";
import { listMembers } from "@/features/team/api";

/** The six "Getting started" steps, in order. Indices are used everywhere. */
export const ONBOARDING_STEP_COUNT = 6;

/**
 * Per-step "done" is DERIVED from data the firm already has — there's no
 * backend per-step tracking (only the global `firm.onboarding_completed`). We
 * fetch the handful of records each step touches once, in parallel, and compute
 * a boolean + a short caption per step. `skipped` is session-only (cleared on
 * reload) since "skip" is a UI affordance, not persisted state.
 */
interface OnboardingProgressState {
  /** done[i] — whether step i looks complete from existing data. */
  done: boolean[];
  /** Short "Done · …" caption per step (null when not done / unknown). */
  doneCaptions: (string | null)[];
  /** Step indices the user skipped this session. */
  skipped: number[];
  loaded: boolean;
  loading: boolean;
  /** Fetch + derive once; a no-op while already loading or loaded. */
  load: () => Promise<void>;
  /** Re-derive after a step saves (bypasses the loaded guard). */
  refresh: () => Promise<void>;
  markSkipped: (i: number) => void;
  unmarkSkipped: (i: number) => void;
}

const EMPTY_DONE = Array<boolean>(ONBOARDING_STEP_COUNT).fill(false);
const EMPTY_CAPTIONS = Array<string | null>(ONBOARDING_STEP_COUNT).fill(null);

async function derive(): Promise<{ done: boolean[]; doneCaptions: (string | null)[] }> {
  const [firm, settings, signatures, status, members] = await Promise.allSettled([
    getFirm(),
    getFirmSettings(),
    listFirmSignatures(),
    getStatus(),
    listMembers(),
  ]);

  const done = [...EMPTY_DONE];
  const captions = [...EMPTY_CAPTIONS];

  // 1 — Company details: firm has a contact method or a mailing address.
  if (firm.status === "fulfilled") {
    const f = firm.value;
    const hasAddress = Boolean(f.addressLine1 && f.city && f.state && f.zipCode);
    if (f.email || f.phone || hasAddress) {
      done[0] = true;
      captions[0] = f.name ? `Done · ${f.name}` : "Done";
    }
  }

  // 2 — Logo & brand colour: a logo has been uploaded (accent colour isn't
  // persisted yet, so we key off the logo).
  if (settings.status === "fulfilled" && settings.value.logoUrl) {
    done[1] = true;
    captions[1] = "Done · logo added";
  }

  // 3 — Signature & letterhead: at least one firm signature exists.
  if (signatures.status === "fulfilled" && signatures.value.length > 0) {
    done[2] = true;
    captions[2] = "Done · signature saved";
  }

  // 4 — Client portal: the portal is switched on.
  if (settings.status === "fulfilled" && settings.value.portalEnabled) {
    done[3] = true;
    captions[3] = settings.value.portalName
      ? `Done · ${settings.value.portalName}`
      : "Done · portal on";
  }

  // 5 — Connect IRS e-Services: a live session is connected.
  if (status.status === "fulfilled" && status.value.state === "CONNECTED") {
    done[4] = true;
    captions[4] = "Done · connected";
  }

  // 6 — Invite your team: more than just the founding admin.
  if (members.status === "fulfilled" && members.value.length > 1) {
    done[5] = true;
    const n = members.value.length - 1;
    captions[5] = `Done · ${n} teammate${n === 1 ? "" : "s"}`;
  }

  return { done, doneCaptions: captions };
}

export const useOnboardingProgressStore = create<OnboardingProgressState>((set, get) => ({
  done: [...EMPTY_DONE],
  doneCaptions: [...EMPTY_CAPTIONS],
  skipped: [],
  loaded: false,
  loading: false,
  load: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true });
    try {
      const { done, doneCaptions } = await derive();
      set({ done, doneCaptions, loaded: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },
  refresh: async () => {
    try {
      const { done, doneCaptions } = await derive();
      set({ done, doneCaptions, loaded: true });
    } catch {
      /* keep the previous snapshot on a transient failure */
    }
  },
  markSkipped: (i) =>
    set((s) => (s.skipped.includes(i) ? s : { skipped: [...s.skipped, i] })),
  unmarkSkipped: (i) => set((s) => ({ skipped: s.skipped.filter((x) => x !== i) })),
}));

/** Derived counts for the hero ring + sidebar pill subtitle. */
export function onboardingProgressSummary(state: {
  done: boolean[];
  skipped: number[];
}): { doneCount: number; skippedCount: number; percent: number } {
  const doneCount = state.done.filter(Boolean).length;
  // A skipped step that later reads as done counts as done, not skipped.
  const skippedCount = state.skipped.filter((i) => !state.done[i]).length;
  return {
    doneCount,
    skippedCount,
    percent: Math.round((doneCount / ONBOARDING_STEP_COUNT) * 100),
  };
}
